import { Injectable } from "@angular/core";
import { ToastController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, tap } from "rxjs/operators";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ToastConnectionCodes, ToastType } from "src/components/shared-components/enum/enumMisc";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";

/**
 * Handle bottom toast service
 */
@Injectable({
  providedIn: "root",
})
export class ToastService implements iTranslation, iEvent {
  private _logContext: string = "Toast.service";

  private _translateSubscription: Subscription = null;
  private _toastConnectionSubscription: Subscription = null;

  private _toastGeneric: any = null;
  private _toastConnectionIssues: HTMLIonToastElement = null;
  private _lblPleaseVerifyConnection: string = "";
  private _isConnectionToastPresent: boolean = false;
  private _isMQTTConnectionFlag: boolean = false;
  private _isAPIConnectionFlag: boolean = false;

  private _ToastCreationPending: boolean = false;

  private _toastConnectionSubject: Subject<ToastConnectionCodes> =
    new Subject<ToastConnectionCodes>();

  public _eventSubscriptions: Array<Subscription> = new Array<Subscription>();

  constructor(
    private _toastController: ToastController,
    private _translateService: TranslateService,
    private _eventService: EventService,
    private _loggerService: LoggerService
  ) {
    this.loadTranslation();
    this.subscribeTranslationEvent();

    this.subscribeEvents();

    this.subscribeToastConnectionSubject();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();

    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }

    if (this._toastConnectionSubscription) {
      this._toastConnectionSubscription.unsubscribe();
    }
  }

  //#region  Translation function
  /**
   * Handle native translation event
   */
  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }

  /**
   * Load translated strings
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.pleaseVerifyConnection"])
      .subscribe((translatedStrings) => {
        this._lblPleaseVerifyConnection =
          translatedStrings["dictionary.pleaseVerifyConnection"];
      });
  }
  //#endregion

  //#region  Event function
  public subscribeEvents(): void {
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        await this.forceClosingConnectionToastOnLogout();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#endregion

  /**
   * Used to show a toast
   * it return instance of toast itsealf
   *
   * @param type Tost type to show
   * @param text Text to show
   * @param duration Duration of the toast in milliseconds
   * @param iconName Toasst icon referecende to Ionic avaiable list
   * @param header (Optional) Header toast value
   * @param extraCssClass (Optional) Extra Css class to apply to toast
   */
  public async showToast(
    type: ToastType,
    text: string,
    duration: number,
    iconName?: string,
    header?: string,
    extraCssClass?: string
  ): Promise<any> {
    await this.closeAllToast();

    if (iconName) {
      this._toastGeneric = await this._toastController.create({
        header,
        message: text,
        duration,
        cssClass: [this.getCssClassByToastType(type), extraCssClass],
        buttons: [
          {
            side: "start",
            icon: iconName,
            handler: () => { },
          },
        ],
      });
    } else {
      this._toastGeneric = await this._toastController.create({
        message: text,
        duration,
        cssClass: [this.getCssClassByToastType(type), extraCssClass],
      });
    }

    this._toastGeneric.onDidDismiss(() => {
      this._toastGeneric = null;
    });

    await this._toastGeneric.present();

    return this._toastGeneric;
  }

  /**
   * Close all toast if someone is open
   */
  public async closeAllToast(): Promise<void> {
    try {
      if (this._toastGeneric) {
        await this._toastGeneric.dismiss();
      }
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "closeAllToast()",
        "Error: " + exception.message
      );
    }
  }

  /**
   * Present toast disconnect issues
   *
   * @param code Code toast
   */
  public async showToastConnectionIssues(
    code: ToastConnectionCodes
  ): Promise<void> {
    this._toastConnectionSubject.next(code);
  }

  /**
   * Dismiss toast connection issues
   *
   * @param code Code toast
   */
  public async closeToastConnectionIssues(
    code: ToastConnectionCodes
  ): Promise<void> {
    this._toastConnectionSubject.next(code);
  }

  /**
   * Handle subject subscription for toast connection
   */
  private subscribeToastConnectionSubject(): void {
    this._toastConnectionSubscription = this._toastConnectionSubject
      .pipe(
        tap((code: ToastConnectionCodes) => {
          this.refreshConnectionFlagByCode(code);
        }),
        debounceTime(1000)
      )
      .subscribe(async (code: ToastConnectionCodes) => {
        await this.handlePresentingDissmissingPopupConnectionIssues(code);
      });
  }

  /**
   * Handle show/dismiss of toast connection issues by specific code
   *
   * @param code Code to handle
   */
  private async handlePresentingDissmissingPopupConnectionIssues(
    code: ToastConnectionCodes
  ): Promise<void> {
    if (!this._isAPIConnectionFlag && !this._isMQTTConnectionFlag) {
      await this.handleCloseToastConnectionIssues();
    } else {
      await this.handleShowToastConnectionIssues(code);
    }
  }

  /**
   * Handle show toast connection issues by specfic code
   *
   * @param code Code to handle
   */
  private async handleShowToastConnectionIssues(
    code: ToastConnectionCodes
  ): Promise<void> {
    const errorMessage: string =
      "ERROR CODE: " + this.getErrorCodeToastConnecting();

    if (!this._isConnectionToastPresent) {
      await this.handleToastConnectionCreation(errorMessage);
    } else if (this._toastConnectionIssues.message !== errorMessage) {
      this._toastConnectionIssues.message = errorMessage;
    }
  }

  /**
   * Handle, if exits, toast dismission
   */
  private async handleCloseToastConnectionIssues(): Promise<void> {
    if (this._isConnectionToastPresent) {
      await this._toastConnectionIssues.dismiss();

      this._toastConnectionIssues = null;
      this._isConnectionToastPresent = false;
    }
  }

  /**
   * Refresh toast service connection tag flags by toast connection code
   *
   * @param code Code to handle
   */
  private refreshConnectionFlagByCode(code: ToastConnectionCodes): void {
    switch (code) {
      case ToastConnectionCodes.API_MQTT_CONNECTION_LOST:
        this._isAPIConnectionFlag = true;
        this._isMQTTConnectionFlag = true;
        break;
      case ToastConnectionCodes.API_CONNECTION_LOST:
        this._isAPIConnectionFlag = true;
        break;
      case ToastConnectionCodes.MQTT_CONNECTION_LOST:
        this._isMQTTConnectionFlag = true;
        break;
      case ToastConnectionCodes.API_CONNECTION_REGAIN:
        this._isAPIConnectionFlag = false;
        break;
      case ToastConnectionCodes.MQTT_CONNECTION_REGAIN:
        this._isMQTTConnectionFlag = false;
        break;
    }
  }

  /**
   * Handle toast creation Ionic native component with specific message
   *
   * @param errorMessage Message to fill into native component
   */
  private async handleToastConnectionCreation(
    errorMessage: string
  ): Promise<void> {
    if (!this._ToastCreationPending) {
      this._ToastCreationPending = true;

      this._toastConnectionIssues = await this._toastController.create({
        header: this._lblPleaseVerifyConnection,
        message: errorMessage,
        cssClass: [
          this.getCssClassByToastType(ToastType.WARNING),
          "content-disconnect-toast",
        ],
        buttons: [
          {
            side: "start",
            icon: "wifi",
            handler: () => { },
          },
        ],
      });

      await this._toastConnectionIssues.present();

      this._isConnectionToastPresent = true;
      this._ToastCreationPending = false;
    }
  }

  //private handleToastConnectionPresenting(code: ToastConnectionCodes, present: boolean): string {
  /**
   * This method cover a double role:
   *
   * 1)Define the precise error code which shall be desplayed inside the message of the toast; this is based on the code received and which flags are
   * currenttly set to true.
   *.
   * @param code A numeric code, associated with a peculiar connection-related situation
   * @returns The error code which is to be displayed inside the toast's message.
   */
  private getErrorCodeToastConnecting(): string {
    let errorCode: string = "";

    if (this._isAPIConnectionFlag && this._isMQTTConnectionFlag) {
      errorCode =
        ToastConnectionCodes[ToastConnectionCodes.API_MQTT_CONNECTION_LOST];
    } else if (this._isAPIConnectionFlag && !this._isMQTTConnectionFlag) {
      errorCode =
        ToastConnectionCodes[ToastConnectionCodes.API_CONNECTION_LOST];
    } else if (!this._isAPIConnectionFlag && this._isMQTTConnectionFlag) {
      errorCode =
        ToastConnectionCodes[ToastConnectionCodes.MQTT_CONNECTION_LOST];
    }

    return errorCode;
  }

  /**
   * Force closing toast.
   * It's used during logout operation to force internal closing avoid nested delay fired from external service
   */
  private async forceClosingConnectionToastOnLogout(): Promise<void> {
    await this.handlePresentingDissmissingPopupConnectionIssues(
      ToastConnectionCodes.MQTT_CONNECTION_REGAIN
    );

    await this.handlePresentingDissmissingPopupConnectionIssues(
      ToastConnectionCodes.API_CONNECTION_REGAIN
    );

    this._ToastCreationPending = false;
  }

  /**
   * Get custom CSS class by toast type
   *
   * @param toastType Tost type to get css class
   * @returns Css class as string
   */
  private getCssClassByToastType(toastType: ToastType): string {
    let cssClass: string = "";

    switch (Number(toastType)) {
      case ToastType.ERROR:
        cssClass = "toast-error";
        break;
      case ToastType.SUCCESS:
        cssClass = "toast-success";
        break;
      case ToastType.WARNING:
        cssClass = "toast-warning";
        break;
      default:
        cssClass = "toast-warning";
        //for default info toast
        break;
    }

    return cssClass;
  }
}
